import { type ErrorInfo, Component, type ReactNode } from 'react';
import { ErrorManagement } from '@printdeal/error-management/react';
import ErrorBoundaryBody from './ErrorBoundaryBody';
import ErrorLayout from './ErrorLayout';

interface Props {
  children: ReactNode;
  inline?: boolean;
}

interface State {
  error?: Error;
}

/**
 * https://docs.sentry.io/platforms/javascript/react/
 *
 * "...One important thing to note about the behavior of error boundaries in development mode is that
 * React will rethrow errors they catch." This will result in errors being reported twice to Sentry with
 * the above setup, but this won’t occur in your production build..."
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      error: undefined,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error });
    ErrorManagement.exception({
      exception: error,
      extra: {
        ...errorInfo,
      },
    });
  }

  render() {
    const {
      children,
      inline = false,
    } = this.props;

    const { error } = this.state;

    if (!error) {
      return children;
    }

    if (inline) {
      return <ErrorBoundaryBody />;
    }

    return <ErrorLayout />;
  }
}

export default ErrorBoundary;
